import React, { useMemo, useRef, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { Button, IconButton, Popover, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { Routes } from "constants/routes";
import { useModals } from "hooks";
import { useSelectBookmarksAll, useUserActions, useUserIdSelector } from "store";
import { IBookmarkType, UrlQueryParams } from "store/types";

import { Group } from "./Group";

const bookmarkRoutes = [Routes.dealReport, Routes.snapshot, Routes.timeline, Routes.classic, Routes.cashflow];

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

export const Bookmark: React.FC = () => {
  const { getBookmarksAction } = useUserActions();
  const userId = useUserIdSelector();
  const bookmarks = useSelectBookmarksAll();

  const { pathname } = useLocation();
  const { saveViewModal } = useModals();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLButtonElement>(null);

  const saveButtonAvailable = useMemo<{ target: keyof UrlQueryParams; type: IBookmarkType } | null>(() => {
    if (matchPath(pathname, Routes.quickView)) {
      return {
        target: "quickViewQueries",
        type: "quick_view"
      };
    }
    if (matchPath(pathname, Routes.myDeals)) {
      return {
        target: "deals",
        type: "deal"
      };
    }
    if (matchPath(pathname, Routes.portfolioConstructionInvestorCorrelation)) {
      return {
        target: "correlationMatrixSettings",
        type: "correlation_matrix"
      };
    }
    if (bookmarkRoutes.some((route) => matchPath(pathname, route))) {
      return {
        target: "reports",
        type: "report"
      };
    }
    if (matchPath(pathname, Routes.monitoring)) {
      return {
        target: "dataOperations",
        type: "data_operations"
      };
    }
    return null;
  }, [pathname]);

  const userBookmarks = useMemo(
    () => bookmarks.filter((bookmark) => bookmark.user_id === userId).sort((a, b) => collator.compare(a.name, b.name)),
    [bookmarks, userId]
  );

  const sharedWithMeBookmarks = useMemo(
    () => bookmarks.filter((bookmark) => bookmark.user_id !== userId).sort((a, b) => collator.compare(a.name, b.name)),
    [bookmarks, userId]
  );

  const hasData = Boolean(userBookmarks.length || sharedWithMeBookmarks.length);

  return (
    <>
      <MaterialTooltip title="Manage Bookmarks: view saved pages or add the current page" isShow>
        <IconButton
          ref={containerRef}
          sx={{
            bgcolor: isOpen ? "#edf1f9" : "#ffffff",
            height: "35px",
            width: "35px"
          }}
          onClick={async () => {
            await getBookmarksAction();
            setIsOpen(true);
          }}
        >
          <BookmarkBorderIcon sx={{ color: "#0a3142" }} />
        </IconButton>
      </MaterialTooltip>
      <Popover
        open={isOpen}
        onClose={() => setIsOpen(false)}
        anchorEl={containerRef.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        sx={{
          ".MuiPopover-paper": {
            backgroundColor: "#ffffff",
            minWidth: "396px",
            minHeight: "266px",
            maxHeight: "517px",
            overflow: "auto",
            borderRadius: "8px",
            boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25)",
            padding: "20px 20px 10px 20px",
            display: "grid",
            gap: "10px",
            top: "48px !important",
            "::-webkit-scrollbar": {
              width: "13px"
            },
            "::-webkit-scrollbar-thumb": {
              backgroundColor: "#bac7ce",
              borderRadius: "8px",
              backgroundClip: "content-box",
              border: "2px solid transparent"
            },
            "::-webkit-scrollbar-track": {
              backgroundColor: "#f2f2f2",
              borderRadius: "8px"
            }
          }
        }}
      >
        {hasData && (
          <>
            <Group
              title="Saved Items"
              bookmarks={userBookmarks}
              closeBookmarksList={() => setIsOpen(false)}
              saveButtonAvailable={saveButtonAvailable}
            />
            <Group
              title="Shared With Me"
              shared
              bookmarks={sharedWithMeBookmarks}
              saveButtonAvailable={saveButtonAvailable}
            />
          </>
        )}
        {!hasData && (
          <Stack sx={{ height: "210px", justifyContent: "space-around", alignItems: "center" }}>
            <BookmarkAddIcon sx={{ color: "#bac7ce", fontSize: 75 }} />
            <Typography fontSize="16px" color="#0a3142">
              There are no saved views yet
            </Typography>
            {saveButtonAvailable && (
              <Button
                onClick={() => {
                  setIsOpen(false);
                  saveViewModal({
                    isOpen: true,
                    target: saveButtonAvailable?.target as keyof UrlQueryParams,
                    type: saveButtonAvailable?.type as IBookmarkType,
                    currentLocation: pathname
                  });
                }}
                variant="contained"
              >
                + Save View
              </Button>
            )}
          </Stack>
        )}
      </Popover>
    </>
  );
};
